/* eslint-disable @atlassian/relay/must-use-inline */
/* eslint-disable @atlassian/relay/unused-fields */
/* eslint-disable jira/import-whitelist */

import { fetchQuery, graphql, type GraphQLTaggedNode } from 'react-relay';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	getTraceIds,
	startCapturingTraceIds,
	stopCapturingTraceIds,
} from '@atlassian/relay-traceid';
import type { AIContextServicePanelResourceResponse } from './ContextPanelServiceTypes.tsx';
import type { StepEnrichmentRequestBuildOptions } from './stepEnrichmentRequest.tsx';
import type {
	graphqlContextPanelQuery as GraphqlContextPanelQuery,
	graphqlContextPanelQuery$data,
} from './__generated__/graphqlContextPanelQuery.graphql.ts';
import { handleGraphQLError, handleGraphQLresponse } from './handleGraphQLError.tsx';

const OPERATION_NAME = 'graphqlContextPanelQuery';

/**
 * Relay may recycle the `fetchQuery().toPromise()` root after the handler returns; step
 * enrichment runs later in React. Clone now so {@link buildStepEnrichmentRequest} still sees
 * `agentAI_contextPanel` when the POST runs.
 */
function snapshotGraphqlDataForDeferredEnrichment(
	data: graphqlContextPanelQuery$data,
): graphqlContextPanelQuery$data {
	try {
		return structuredClone(data);
	} catch {
		try {
			const jsonRoundTrip: graphqlContextPanelQuery$data = JSON.parse(JSON.stringify(data));
			return jsonRoundTrip;
		} catch {
			return data;
		}
	}
}

export const graphqlContextPanelQuery: GraphQLTaggedNode = graphql`
	query graphqlContextPanelQuery($cloudId: ID!, $issueId: String) {
		agentAI_contextPanel(issueId: $issueId, cloudId: $cloudId) {
			__typename
			... on AgentAIContextPanelResponse {
				summary
				agentSteps
				reporterDetails
				suggestedActions {
					type
					content {
						title
						description
					}
					context {
						fieldId
						fieldType
						fieldName
						suggestion
						currentValue
					}
				}
				suggestedNextSteps {
					citations {
						id
						sourceId
						sourceType
						sourceUrl
						title
					}
					nextSteps {
						content
						inlineAction {
							type
							draftReplyPill {
								category
								example
								goal
								intent
								pillId
								text
								triggerSummary
							}
						}
					}
				}
			}
			... on QueryError {
				message
				extensions {
					statusCode
					errorType
				}
			}
		}
	}
`;

export const getContextPanelSuggestions = async (
	cloudId: string,
	issueId: string,
	stepEnrichmentBuildOptions?: StepEnrichmentRequestBuildOptions,
): Promise<AIContextServicePanelResourceResponse> => {
	startCapturingTraceIds(OPERATION_NAME);
	const graphQLResponse = await fetchQuery<GraphqlContextPanelQuery>(
		getRelayEnvironment(),
		graphqlContextPanelQuery,
		{
			cloudId,
			issueId,
		},
	)
		.toPromise()
		.then((data) => {
			const traceIds = getTraceIds(OPERATION_NAME);
			const variables = { cloudId, issueId };
			const result = handleGraphQLresponse(data, traceIds);
			if (
				result.type === 'ServicePanelSuggestions' &&
				data?.agentAI_contextPanel?.__typename === 'AgentAIContextPanelResponse' &&
				fg('jsm_ai_context_panel_step_enrichment')
			) {
				return {
					...result,
					stepEnrichmentDeferredInput: {
						graphqlData: snapshotGraphqlDataForDeferredEnrichment(data),
						variables,
						buildOptions: stepEnrichmentBuildOptions,
					},
				};
			}
			return result;
		})
		.catch((error) => {
			const traceIds = getTraceIds(OPERATION_NAME);
			return handleGraphQLError(error, traceIds);
		})
		.finally(() => {
			stopCapturingTraceIds(OPERATION_NAME);
		});

	return graphQLResponse;
};
